import { createFileRoute, Navigate, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { TranslationsPage, type TranslationTab } from '@pages/translations';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessTranslationLanguages, canAccessTranslations } from '@shared/lib';

const standardTranslationTabs = ['glossary', 'content', 'interface'] as const;

function availableTranslationTabs(ability: RouterContext['ability']): readonly TranslationTab[] {
  return canAccessTranslationLanguages(ability)
    ? [...standardTranslationTabs, 'languages']
    : standardTranslationTabs;
}

export const Route = createFileRoute('/$locale/_authenticated/translations')({
  validateSearch: z.object({
    tab: z.enum(['glossary', 'content', 'interface', 'languages']).optional(),
  }),
  beforeLoad: ({ context, params, search }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessTranslations(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
    if (search.tab === 'languages' && !canAccessTranslationLanguages(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/translations',
        params: { locale: params.locale },
        search: {},
        replace: true,
      });
    }
  },
  component: TranslationsRoute,
});

function TranslationsRoute() {
  const { locale } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const auth = useAuth();
  const availableTabs = availableTranslationTabs(ability);
  const requestedTab = search.tab ?? 'glossary';

  if (!auth.isLoading && auth.isAuthenticated && !canAccessTranslations(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  if (!availableTabs.includes(requestedTab)) {
    return <Navigate to="/$locale/translations" params={{ locale }} search={{}} replace />;
  }

  return (
    <TranslationsPage
      activeTab={requestedTab}
      availableTabs={availableTabs}
      onTabChange={(tab: TranslationTab) => {
        void navigate({
          replace: true,
          search: { tab: tab === 'glossary' ? undefined : tab },
        });
      }}
    />
  );
}
